// Единая точка HTTP-запросов к backend админ-панели. В отличие от miniapp/src/api/client.ts
// (X-Telegram-Init-Data на каждый запрос), здесь — `Authorization: Bearer <JWT>` из useAuthStore
// на каждый запрос, КРОМЕ /admin/login (токена ещё нет — заголовок просто не добавляется, сам
// backend-роут его и не требует, см. app/api/routes/admin/auth.py).

import { useAuthStore } from "../store/authStore";
import { extractErrorMessage } from "./errorMessage";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (response.status === 401 && path !== "/admin/login") {
    // Токен просрочен/отозван — тот же эффект, что и явный logout: ProtectedRoute (App.tsx)
    // перекидывает на LoginScreen, без отдельного обработчика на каждом экране. На сам /login
    // 401 значит "неверный логин/пароль", а не "токен истёк" — логаутить как обычно, но токена и
    // так ещё не было (setToken вызывается только при успехе), реального эффекта нет.
    useAuthStore.getState().logout();
  }

  if (!response.ok) {
    const body = await response.text();
    throw new ApiError(response.status, extractErrorMessage(body, response.statusText));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
};

export interface LoginResponse {
  access_token: string;
  token_type: string;
}

export const login = (username: string, password: string) =>
  api.post<LoginResponse>("/admin/login", { username, password });

// Casing и состав полей — как у backend Pydantic-схем (app/schemas/*.py), snake_case, не
// camelCase — тот же принцип, что и в miniapp/src/api/client.ts.

export interface Plan {
  id: number;
  code: string;
  title: string;
  price: number;
  currency: string;
  duration_days: number;
  device_limit: number;
  is_trial: boolean;
  is_bundle: boolean;
}

export type SubscriptionStatus = "active" | "expiring" | "expired" | "cancelled";

export interface Device {
  id: number;
  name: string;
  os: string | null;
  attached_at: string;
  last_seen_at: string | null;
  is_primary: boolean;
  is_online: boolean;
}

// Урезанная версия Subscription (без своего bundle_partner) — только для "второй половины" пары
// "Расширенный" внутри Subscription.bundle_partner ниже, см. app/schemas/subscription.py.
export interface SubscriptionSummary {
  id: number;
  status: SubscriptionStatus;
  plan: Plan;
  started_at: string;
  expires_at: string;
  auto_renew: boolean;
  devices: Device[];
  device_limit: number;
}

export interface Subscription extends SubscriptionSummary {
  bundle_partner: SubscriptionSummary | null;
}

export type PaymentStatus = "pending" | "succeeded" | "failed" | "refunded";
export type PaymentType = "purchase" | "renewal" | "upgrade" | "refund";
export type PaymentProvider = "telegram" | "acquiring";

// AdminPaymentOut (app/schemas/admin_user.py) — расширяет PaymentOut Mini App'а
// subscription_id/provider_payment_id, ненужными обычному пользователю, но нужными админу.
export interface AdminPayment {
  id: number;
  amount: number;
  currency: string;
  status: PaymentStatus;
  type: PaymentType;
  provider: PaymentProvider;
  provider_payment_id: string;
  subscription_id: number;
  created_at: string;
}

export interface AdminUserSummary {
  id: number;
  telegram_id: number;
  username: string | null;
  is_active: boolean;
  created_at: string;
}

export interface AdminUserDetail extends AdminUserSummary {
  role: "client" | "admin";
  // ВСЯ история подписок (не только активные, в отличие от Mini App) — см. докстринг
  // app/api/routes/admin/users.py.
  subscriptions: Subscription[];
  payments: AdminPayment[];
}

export const searchUsers = (query: string) =>
  api.get<AdminUserSummary[]>(`/admin/users?q=${encodeURIComponent(query)}`);
export const fetchUserCard = (userId: number) =>
  api.get<AdminUserDetail>(`/admin/users/${userId}`);

// Этап 6, раздел 3 «Лицензии» (docs/PLAN.md) — станция AV-Desk отдельной подписки. Backend не
// отдаёт av_license_id в SubscriptionOut (Mini App его не показывает), поэтому фронтенд не может
// заранее знать, есть ли уже станция — все три кнопки показаны всегда, backend сам отвечает 409
// с понятным текстом, если действие не подходит текущему состоянию (см.
// app/services/admin_licenses.py).
export const issueLicense = (subscriptionId: number) =>
  api.post<Subscription>(`/admin/subscriptions/${subscriptionId}/issue-license`);
export const revokeLicense = (subscriptionId: number) =>
  api.post<Subscription>(`/admin/subscriptions/${subscriptionId}/revoke-license`);
export const renewLicense = (subscriptionId: number, extraDays: number) =>
  api.post<Subscription>(`/admin/subscriptions/${subscriptionId}/renew-license`, {
    extra_days: extraDays,
  });

// Этап 6, раздел 4 «Тарифы» (docs/PLAN.md) — CRUD напрямую над Plan. Нет DELETE — архивация
// через is_active, та же семантика, что и раньше (app/models/plan.py).
export interface AdminPlan {
  id: number;
  code: string;
  title: string;
  price: number;
  currency: string;
  duration_days: number;
  device_limit: number;
  is_trial: boolean;
  is_active: boolean;
  is_bundle: boolean;
  av_tariff_group_id: string | null;
  bundle_secondary_tariff_group_id: string | null;
}

export interface AdminPlanCreateInput {
  code: string;
  title: string;
  price: number;
  currency?: string;
  duration_days: number;
  device_limit: number;
  is_trial?: boolean;
  is_active?: boolean;
  av_tariff_group_id?: string | null;
  bundle_secondary_tariff_group_id?: string | null;
}

// `code` не редактируется (см. app/schemas/admin_plan.py::AdminPlanUpdate) — исключён через Omit,
// не просто "необязателен", чтобы TypeScript не позволил случайно его передать при обновлении.
export type AdminPlanUpdateInput = Partial<Omit<AdminPlanCreateInput, "code">>;

export const fetchPlans = () => api.get<AdminPlan[]>("/admin/plans");
export const createPlan = (input: AdminPlanCreateInput) => api.post<AdminPlan>("/admin/plans", input);
export const updatePlan = (planId: number, input: AdminPlanUpdateInput) =>
  api.patch<AdminPlan>(`/admin/plans/${planId}`, input);

// Этап 6, раздел 5 «Промокоды» (docs/PLAN.md) — только FREE_PERIOD (пробный доступ), та же логика,
// что у scripts/generate_promo_code.py. Деактивация — БЕЗ отдельного поля is_active в модели,
// backend просто сдвигает valid_to на "сейчас" (app/api/routes/admin/promo_codes.py).
export interface AdminPromoCode {
  id: number;
  code: string;
  discount_type: "percent" | "fixed" | "free_period";
  discount_value: number;
  valid_from: string;
  valid_to: string;
  is_expired: boolean;
  plan_titles: string[];
  is_used: boolean;
  used_at: string | null;
  used_by_telegram_id: number | null;
  used_by_username: string | null;
}

export interface AdminPromoCodeCreateInput {
  plan_id: number;
  days: number;
  valid_days: number;
}

export const fetchPromoCodes = () => api.get<AdminPromoCode[]>("/admin/promo-codes");
export const createPromoCode = (input: AdminPromoCodeCreateInput) =>
  api.post<AdminPromoCode>("/admin/promo-codes", input);
export const deactivatePromoCode = (promoId: number) =>
  api.post<AdminPromoCode>(`/admin/promo-codes/${promoId}/deactivate`);

// Этап 6, раздел 6 «Возвраты» (docs/PLAN.md) — ТОЛЬКО пометка в БД (Payment.status=REFUNDED),
// без реального возврата денег через провайдера. Кнопка живёт на уже существующей таблице
// платежей в UserCardScreen.tsx (components/PaymentRow.tsx) — отдельного списка платежей нет.
export interface AdminRefundResult {
  payment: AdminPayment;
  license_revoked: boolean;
  license_revoke_error: string | null;
}

export const refundPayment = (paymentId: number, revokeLicense: boolean) =>
  api.post<AdminRefundResult>(`/admin/payments/${paymentId}/refund`, {
    revoke_license: revokeLicense,
  });
